import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import MapView from "@/components/MapView";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, CalendarClock, Truck, Route, Zap } from "lucide-react";

export default function Scheduler() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [depot, setDepot] = useState(null);
  const [dueSites, setDueSites] = useState([]);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [weekBusy, setWeekBusy] = useState(false);
  const [weekWeeks, setWeekWeeks] = useState(1);
  const [weekResult, setWeekResult] = useState(null);

  useEffect(() => {
    api.get("/settings/depot").then((r) => setDepot(r.data));
  }, []);

  const loadDue = () => {
    api.get(`/scheduler/due?date=${date}`).then((r) => setDueSites(r.data.sites || []));
    setPlan(null);
  };

  useEffect(() => { loadDue(); /* eslint-disable-line */ }, [date]);

  const runPlan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/scheduler/plan?date=${date}`);
      setPlan(data);
      toast.success("AI plan ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Plan failed");
    } finally { setBusy(false); }
  };

  const materialise = async () => {
    if (!plan) return;
    setCreating(true);
    try {
      for (const drv of plan.drivers || []) {
        if (!drv.stops?.length) continue;
        await api.post("/trips", {
          driver: drv.driver,
          vehicle: drv.vehicle,
          trip_date: date,
          start_time: drv.start_time || "08:00",
          end_time: drv.end_time || "16:00",
          stops: drv.stops.map((s) => {
            const src = dueSites.find((x) => x.id === s.site_id);
            return {
              client_id: src?.client_id, site_id: s.site_id,
              waste_type: null, notes: s.notes || null,
            };
          }),
          notes: plan.summary,
        });
      }
      toast.success("Trips created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create trips");
    } finally { setCreating(false); }
  };

  const planWeek = async () => {
    setWeekBusy(true);
    setWeekResult(null);
    try {
      // idempotently fill recurring pickups first, then run the AI weekly planner
      await api.post(`/pickups/generate-recurring?weeks=${weekWeeks}`);
      const { data } = await api.post(`/scheduler/plan-week?weeks=${weekWeeks}`);
      setWeekResult(data);
      const msg = `${data.trips_created} trip${data.trips_created === 1 ? "" : "s"} · ${data.pickups_scheduled} pickup${data.pickups_scheduled === 1 ? "" : "s"} scheduled` +
        (data.trips_skipped ? ` · ${data.trips_skipped} already existed` : "");
      toast.success(msg);
      loadDue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Weekly plan failed");
    } finally { setWeekBusy(false); }
  };

  const mapSites = dueSites.map((s) => ({ ...s, _due: true }));

  return (
    <div>
      <PageHeader
        eyebrow="Operations · Scheduler"
        title="Daily plan"
        subtitle="Auto-detect which sites are due today, then let Claude order stops, assign drivers and vehicles, and estimate durations."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 pr-3 border-r" style={{ borderColor: "var(--border)" }}>
              <select
                value={weekWeeks}
                onChange={(e) => setWeekWeeks(Number(e.target.value))}
                className="hbt-input py-1.5 text-xs max-w-[90px]"
                data-testid="plan-week-weeks"
              >
                {[1, 2, 3, 4].map((w) => <option key={w} value={w}>{w} week{w > 1 ? "s" : ""}</option>)}
              </select>
              <button
                onClick={planWeek}
                disabled={weekBusy}
                className="btn-outline inline-flex items-center gap-2"
                data-testid="plan-week-btn"
                title="Fill recurring pickups, run the AI planner for every day in the window, and create driver trips."
              >
                <Zap className={`w-4 h-4 ${weekBusy ? "animate-pulse" : ""}`} /> {weekBusy ? "Planning…" : "Plan week"}
              </button>
            </div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="hbt-input max-w-[180px]" data-testid="scheduler-date" />
            <button onClick={runPlan} disabled={busy || dueSites.length === 0} className="btn-primary inline-flex items-center gap-2" data-testid="run-ai-plan">
              <Sparkles className="w-4 h-4" /> {busy ? "Planning…" : "Run AI plan"}
            </button>
          </div>
        }
      />
      <div className="px-8 lg:px-12 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="hbt-card p-5"><div className="hbt-label">Due today</div><div className="font-display text-3xl font-semibold mt-2">{dueSites.length}</div><div className="text-xs text-neutral-500 mt-1">sites need collection</div></div>
          <div className="hbt-card p-5"><div className="hbt-label">Drivers assigned</div><div className="font-display text-3xl font-semibold mt-2">{plan?.drivers?.filter(d=>d.stops?.length).length ?? "—"}</div></div>
          <div className="hbt-card p-5"><div className="hbt-label">Est. total minutes</div><div className="font-display text-3xl font-semibold mt-2 font-mono-num">{plan ? (plan.drivers||[]).reduce((s,d)=>s+(d.total_duration_minutes||0),0) : "—"}</div></div>
        </div>

        <MapView depot={depot} sites={mapSites} height={420} />

        {weekResult && (
          <div className="hbt-card p-6" data-testid="plan-week-result">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="hbt-label flex items-center gap-1.5"><Zap className="w-3 h-3" /> Weekly plan</div>
                <div className="font-display text-xl font-semibold mt-1">
                  {weekResult.trips_created} new trip{weekResult.trips_created === 1 ? "" : "s"}
                  <span className="text-neutral-500 font-normal"> · {weekResult.pickups_scheduled} pickup{weekResult.pickups_scheduled === 1 ? "" : "s"} scheduled</span>
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {weekResult.window.start} → {weekResult.window.end}
                  {weekResult.trips_skipped ? ` · ${weekResult.trips_skipped} trips already existed and were skipped` : ""}
                </div>
              </div>
              <button onClick={() => setWeekResult(null)} className="text-xs text-neutral-500 hover:text-black" data-testid="plan-week-dismiss">Dismiss</button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
              {(weekResult.per_day || []).map((d) => (
                <div key={d.date} className="border rounded-md p-2.5 text-center" style={{ borderColor: "var(--border)" }} data-testid={`plan-week-day-${d.date}`}>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">{new Date(d.date).toLocaleDateString(undefined, { weekday: "short" })}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{d.date.slice(5)}</div>
                  <div className="font-display text-lg font-semibold mt-1" style={{ color: d.trips_created > 0 ? "var(--forest)" : "var(--muted)" }}>
                    {d.trips_created}
                  </div>
                  <div className="text-[10px] text-neutral-400">trip{d.trips_created === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {plan && (
          <div className="hbt-card p-6" data-testid="ai-plan-card">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <div className="hbt-label">AI plan · {plan.date}</div>
                <div className="font-display text-xl font-semibold mt-1">{plan.summary}</div>
              </div>
              <button onClick={materialise} disabled={creating} className="btn-accent inline-flex items-center gap-2" data-testid="materialise-plan">
                <Route className="w-4 h-4" /> {creating ? "Creating…" : "Create trips"}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(plan.drivers || []).filter(d => d.stops?.length).map((d) => (
                <div key={`${d.driver}-${d.vehicle}`} className="border rounded-md p-4" style={{ borderColor: "var(--border)" }} data-testid={`plan-driver-${d.driver}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Truck className="w-4 h-4" style={{ color: "var(--forest)" }} />
                    <div className="font-semibold">{d.driver} · <span className="text-neutral-500">{d.vehicle}</span></div>
                  </div>
                  <div className="text-xs text-neutral-500 mb-3">{d.start_time}–{d.end_time} · {d.total_duration_minutes}min</div>
                  <ol className="space-y-1 text-xs">
                    {d.stops.map((s, si) => (
                      <li key={s.site_id || `${d.driver}-${si}`} className="flex items-start gap-2 py-1 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
                        <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: "rgba(26,54,45,0.08)", color: "var(--forest)" }}>{si+1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{s.site_name}</div>
                          <div className="text-neutral-500 truncate">{s.client_name} · {s.estimated_minutes}min</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        )}

        {dueSites.length === 0 && (
          <div className="hbt-card p-12 text-center text-neutral-500">
            <CalendarClock className="w-6 h-6 mx-auto mb-3 opacity-40" />
            No sites scheduled for {date}.
          </div>
        )}
      </div>
    </div>
  );
}
